/**
 * ConfirmDialog Component
 * Modal confirmation dialog: optional title bar with close button,
 * question icon with message, and cancel/confirm buttons
 */

import { i18n } from '../utils/i18n.js';

export class ConfirmDialog {
  constructor({ container, title = '', message, confirmLabel, cancelLabel, onConfirm, onCancel }) {
    this.container = container;
    this.title = title;
    this.message = message;
    this.confirmLabel = confirmLabel;
    this.cancelLabel = cancelLabel;
    this.onConfirm = onConfirm;
    this.onCancel = onCancel;
    this.element = null;
    this.previouslyFocused = null;
    this.handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close(this.onCancel);
      }
    };
  }

  render() {
    this.previouslyFocused = document.activeElement;

    const header = this.title
      ? `
        <div class="flex items-center justify-between gap-3 px-4 py-3 bg-gray-100 border-b border-gray-200">
          <h2 id="wsp-confirm-title" class="text-sm font-semibold text-gray-800">${this.title}</h2>
          <button type="button" id="wsp-confirm-close" class="p-1 text-gray-600 rounded hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400" title="${i18n.t('button.close')}" aria-label="${i18n.t('button.close')}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>`
      : '';

    // Appended (not innerHTML on container) so the panel content is preserved
    this.element = document.createElement('div');
    this.element.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50';
    this.element.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl mx-4 max-w-2xl w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="${this.title ? 'wsp-confirm-title' : 'wsp-confirm-message'}" aria-describedby="wsp-confirm-message">
        ${header}
        <div class="flex items-center gap-4 px-6 py-5">
          <svg class="w-10 h-10 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p id="wsp-confirm-message" class="text-gray-800 text-sm">${this.message}</p>
        </div>
        <div class="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <button type="button" id="wsp-confirm-cancel" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
            ${this.cancelLabel}
          </button>
          <button type="button" id="wsp-confirm-accept" class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            ${this.confirmLabel}
          </button>
        </div>
      </div>
    `;

    this.container.appendChild(this.element);
    this.attachEventListeners();
    this.element.querySelector('#wsp-confirm-accept').focus();
  }

  attachEventListeners() {
    this.element.querySelector('#wsp-confirm-accept').addEventListener('click', () => this.close(this.onConfirm));
    this.element.querySelector('#wsp-confirm-cancel').addEventListener('click', () => this.close(this.onCancel));
    this.element.querySelector('#wsp-confirm-close')?.addEventListener('click', () => this.close(this.onCancel));

    // Backdrop click cancels
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.close(this.onCancel);
    });

    document.addEventListener('keydown', this.handleKeyDown);
  }

  close(callback) {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.element.remove();
    this.previouslyFocused?.focus?.();
    if (callback) callback();
  }
}
